import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../api';
import { IoMailOpenOutline, IoPlayCircleOutline, IoRefreshOutline, IoSearchOutline } from 'react-icons/io5';

const STATUS_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'sent', label: 'Enviado' },
  { value: 'sent_on_retry', label: 'Reintento OK' },
  { value: 'skipped', label: 'Omitido' },
  { value: 'failed', label: 'Fallido' },
  { value: 'failed_permanent', label: 'Fallido permanente' },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Todos' },
  { value: 'workout', label: 'Entrenamiento' },
  { value: 'measurement', label: 'Medición' },
];

const CoachReminders = () => {
  const [reminderClients, setReminderClients] = useState([]);
  const [reminderHistory, setReminderHistory] = useState([]);

  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');

  const [historyPage, setHistoryPage] = useState(1);
  const [historyLimit] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);

  const [clientSearch, setClientSearch] = useState('');
  const [selectedClientId, setSelectedClientId] = useState('');
  const [manualReminderType, setManualReminderType] = useState('workout');
  const [manualLoading, setManualLoading] = useState(false);
  const [reminderLoading, setReminderLoading] = useState(false);
  const [loading, setLoading] = useState(true);

  const selectedClient = useMemo(
    () => reminderClients.find((c) => String(c.client_id) === String(selectedClientId)) || null,
    [reminderClients, selectedClientId]
  );

  const filteredClients = useMemo(() => {
    const q = clientSearch.trim().toLowerCase();
    if (!q) return reminderClients;
    return reminderClients.filter((c) =>
      c.client_name.toLowerCase().includes(q) || c.client_email.toLowerCase().includes(q)
    );
  }, [clientSearch, reminderClients]);

  const fetchReminderHistory = async (
    page = historyPage,
    status = statusFilter,
    type = typeFilter,
    userSearch = nameFilter,
    dateFrom = filterDateFrom,
    dateTo = filterDateTo
  ) => {
    const params = new URLSearchParams({ limit: String(historyLimit), page: String(page) });
    if (status) params.append('status', status);
    if (type) params.append('reminder_type', type);
    if (userSearch) params.append('user_search', userSearch);
    if (dateFrom) params.append('date_from', dateFrom);
    if (dateTo) params.append('date_to', dateTo);

    const historyRes = await api.get(`/coach/reminders/history?${params.toString()}`).catch(() => ({ data: [] }));
    const rows = historyRes.data || [];
    setReminderHistory(rows);
    setHasNextPage(rows.length === historyLimit);
    setHistoryPage(page);
  };

  useEffect(() => {
    const load = async () => {
      try {
        const [clientsRes, historyRes] = await Promise.all([
          api.get('/coach/reminders/clients'),
          api.get('/coach/reminders/history?limit=10&page=1'),
        ]);

        const clients = clientsRes.data || [];
        setReminderClients(clients);
        if (clients.length > 0) {
          setSelectedClientId(String(clients[0].client_id));
        }

        const initialRows = historyRes.data || [];
        setReminderHistory(initialRows);
        setHistoryPage(1);
        setHasNextPage(initialRows.length === historyLimit);
      } catch {
        toast.error('No se pudieron cargar los recordatorios.');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [historyLimit]);

  const runRemindersNow = async () => {
    setReminderLoading(true);
    try {
      const runRes = await api.post('/coach/reminders/run-now');
      const s = runRes.data;
      toast.success(`Lote ejecutado: ${s.sent} enviados, ${s.failed} fallidos, ${s.skipped} omitidos`);
      await fetchReminderHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo ejecutar el lote de recordatorios');
    } finally {
      setReminderLoading(false);
    }
  };

  const sendManualReminder = async () => {
    if (!selectedClientId) {
      toast.error('Selecciona un cliente');
      return;
    }

    setManualLoading(true);
    try {
      const res = await api.post(`/coach/reminders/send/${selectedClientId}`, {
        reminder_type: manualReminderType,
      });
      const name = res?.data?.user_name || 'cliente';
      if (res?.data?.status === 'sent') {
        toast.success(`Recordatorio enviado a ${name}`);
      } else {
        toast.error(`No se pudo enviar a ${name}`);
      }
      await fetchReminderHistory();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo enviar recordatorio manual');
    } finally {
      setManualLoading(false);
    }
  };

  const applyFilters = async () => {
    await fetchReminderHistory(1, statusFilter, typeFilter, nameFilter, filterDateFrom, filterDateTo);
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto w-full space-y-3">
        {[...Array(3)].map((_, i) => (
          <div key={i} className="skeleton h-28 rounded-xl" />
        ))}
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto w-full space-y-6 fade-up">
      <div>
        <h1 className="text-3xl md:text-4xl font-display font-bold text-white">Recordatorios</h1>
        <p className="text-gray-400 text-sm mt-1">Todo lo de recordatorios en un solo lugar: manual, lote e historial.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
        <div className="xl:col-span-2 glass-panel p-5 rounded-xl space-y-3">
          <div className="flex items-center gap-2 text-white font-semibold">
            <IoMailOpenOutline className="text-rose-400" />
            Envío Manual
          </div>

          <div className="text-xs text-gray-400 bg-black/20 border border-white/10 rounded-lg p-3">
            La asignación de clientes se gestiona en el módulo de Clientes para evitar redundancias.
          </div>

          <div className="relative">
            <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              type="text"
              value={clientSearch}
              onChange={(e) => setClientSearch(e.target.value)}
              placeholder="Buscar cliente por nombre o correo"
              className="w-full bg-white/5 border border-white/10 text-gray-200 text-sm rounded-lg pl-10 pr-3 py-2 focus:outline-none focus:border-red-500"
            />
          </div>

          <div className="max-h-52 overflow-auto space-y-1 pr-1">
            {filteredClients.length === 0 ? (
              <p className="text-sm text-gray-500">Sin clientes vinculados al coach.</p>
            ) : (
              filteredClients.map((c) => {
                const active = String(c.client_id) === String(selectedClientId);
                return (
                  <button
                    key={c.client_id}
                    type="button"
                    onClick={() => setSelectedClientId(String(c.client_id))}
                    className={`w-full text-left rounded-lg px-3 py-2 border transition-colors ${
                      active
                        ? 'bg-rose-900/40 border-rose-500/60 text-white'
                        : 'bg-white/5 border-white/10 text-gray-300 hover:bg-white/10'
                    }`}
                  >
                    <p className="text-sm font-semibold truncate">{c.client_name}</p>
                    <p className="text-xs text-gray-400 truncate">{c.client_email}</p>
                  </button>
                );
              })
            )}
          </div>

          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setManualReminderType('workout')}
              className={`rounded-lg px-3 py-2 text-sm font-semibold border ${
                manualReminderType === 'workout'
                  ? 'bg-red-800/40 border-red-500/60 text-white'
                  : 'bg-white/5 border-white/10 text-gray-300'
              }`}
            >
              Entrenamiento
            </button>
            <button
              type="button"
              onClick={() => setManualReminderType('measurement')}
              className={`rounded-lg px-3 py-2 text-sm font-semibold border ${
                manualReminderType === 'measurement'
                  ? 'bg-blue-800/40 border-blue-500/60 text-white'
                  : 'bg-white/5 border-white/10 text-gray-300'
              }`}
            >
              Medición
            </button>
          </div>

          <button
            type="button"
            onClick={sendManualReminder}
            disabled={manualLoading || !selectedClient}
            className="w-full inline-flex items-center justify-center gap-2 bg-rose-900 hover:bg-rose-800 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            {manualLoading ? <IoRefreshOutline className="animate-spin" size={16} /> : <IoMailOpenOutline size={16} />}
            {manualLoading ? 'Enviando...' : `Enviar a ${selectedClient?.client_name || 'cliente'}`}
          </button>

          <button
            type="button"
            onClick={runRemindersNow}
            disabled={reminderLoading}
            className="w-full inline-flex items-center justify-center gap-2 bg-rose-700 hover:bg-rose-600 disabled:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-semibold"
          >
            {reminderLoading ? <IoRefreshOutline className="animate-spin" size={16} /> : <IoPlayCircleOutline size={16} />}
            {reminderLoading ? 'Ejecutando lote...' : 'Ejecutar lote automático'}
          </button>
        </div>

        <div className="xl:col-span-3 glass-panel p-5 rounded-xl space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <h2 className="text-base font-semibold text-white">Historial</h2>
            <p className="text-xs text-gray-400">Página {historyPage}</p>
          </div>

          <div className="space-y-2">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value || 'all-status'}
                  type="button"
                  onClick={() => setStatusFilter(s.value)}
                  className={`text-xs rounded-lg px-2 py-2 border ${
                    statusFilter === s.value ? 'bg-white/15 border-white/30 text-white' : 'bg-white/5 border-white/10 text-gray-300'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-3 gap-2">
              {TYPE_OPTIONS.map((t) => (
                <button
                  key={t.value || 'all-type'}
                  type="button"
                  onClick={() => setTypeFilter(t.value)}
                  className={`text-xs rounded-lg px-2 py-2 border ${
                    typeFilter === t.value ? 'bg-white/15 border-white/30 text-white' : 'bg-white/5 border-white/10 text-gray-300'
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
            <input
              type="text"
              value={nameFilter}
              onChange={(e) => setNameFilter(e.target.value)}
              placeholder="Buscar en historial por nombre/email"
              onKeyDown={(e) => e.key === 'Enter' && applyFilters()}
              className="w-full bg-white/5 border border-white/10 text-gray-200 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
            />
            <div className="grid grid-cols-2 gap-2">
              <input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="bg-white/5 border border-white/10 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
              />
              <input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="bg-white/5 border border-white/10 text-gray-300 text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-red-500"
              />
            </div>
            <button
              type="button"
              onClick={applyFilters}
              className="inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-lg text-sm font-semibold"
            >
              Aplicar filtros
            </button>
          </div>

          {reminderHistory.length > 0 ? (
            <div className="space-y-1 max-h-80 overflow-auto pr-1">
              {reminderHistory.map((r) => (
                <div key={r.id} className="text-xs text-gray-300 bg-white/5 rounded px-3 py-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-semibold truncate">{r.user_name || r.user_email || `#${r.user_id}`}</span>
                    <span className="text-gray-500">{new Date(r.sent_at).toLocaleString('es-ES')}</span>
                  </div>
                  <div className="mt-1">
                    <span className="text-gray-500">{r.reminder_type} · </span>
                    <span className={r.status === 'sent' || r.status === 'sent_on_retry' ? 'text-green-300' : r.status.includes('failed') ? 'text-red-300' : 'text-yellow-300'}>{r.status}</span>
                    {r.message ? <span className="text-gray-500"> · {r.message}</span> : null}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400">No hay resultados para los filtros actuales.</p>
          )}

          <div className="pt-1 flex items-center justify-between">
            <button
              type="button"
              onClick={() => fetchReminderHistory(Math.max(1, historyPage - 1))}
              disabled={historyPage <= 1}
              className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 disabled:bg-white/5 disabled:text-gray-600"
            >
              Anterior
            </button>
            <button
              type="button"
              onClick={() => fetchReminderHistory(historyPage + 1)}
              disabled={!hasNextPage}
              className="text-xs px-3 py-1.5 rounded bg-white/10 hover:bg-white/20 disabled:bg-white/5 disabled:text-gray-600"
            >
              Siguiente
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CoachReminders;
